import React, { useState } from 'react';
import classNames from 'classnames';
import { FORM_ERROR, FormApi } from 'final-form';
import { useFormState } from 'react-final-form';
import { Prompt } from 'react-router-dom';
import { Button } from '@tcl-boron-prefabs/button';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import {
  FINANCIAL_ASSISTANCE_FORM_V5_KEY,
  FinancialAssistanceCalculationStatus,
  RELATIONSHIP_TO_PATIENT_NAME_V4,
} from '@tempus/patient-forms-service-shared';
import { NavigationFormProps } from '../../../custom-to-do-list/components/NavigatorChecklist';
import { FinancialAidDto, FormProps, submitFormGeneric } from '../../forms';
import Form from '../../shared/Form';
import FormAlert, { AlertStatus } from '../../shared/FormAlert';
import FormBannerError from '../../shared/FormBannerError';
import { FormTableField } from '../../shared/FormTable';
import Logo from '../../shared/Logo';
import { StatusBadgeProps } from '../../shared/StatusBadge';
import {
  LEAVE_FORM_WARNING,
  deleteFalsyStringFields,
  deleteUnmetConditionalFields,
  setReadOnlyValidatedValuesOnSubmit,
} from '../../shared/utils';
import ConsentToApplicationV5Table, { consentToApplicationV5Fields } from './ConsentToApplicationV5Table';
import ExtenuatingCircumstancesV5Table, { extenuatingCircumstancesV5Fields } from './ExtenuatingCircumstancesV5Table';
import OrderingPhysicianV5Table, { orderingPhysicianV5Fields } from './OrderingPhysicianV5Table';
import PatientInformationV5Table, { patientInformationV5Fields } from './PatientInformationV5Table';

import styles from '../../shared/styles.module.css';
import { useQuery } from '../../../../api/hooks';
import { FAOncoNeuroSelector } from '../shared/OncoNeuroSelector';
import { PatientFormsTitleInjection } from '../../../custom-to-do-list/components/PatientFormsTitleInjection';
import { getOktaAccessToken } from '../../../../api/Okta';
import { requiresInternalAuthentication } from '../shared/utils/requiresInternalAuthentication';

export const FINANCIAL_ASSISTANCE_V5_KEY = FINANCIAL_ASSISTANCE_FORM_V5_KEY;

type FinancialAidV5FormProps = FormProps & {
  statement: string;
  status: FinancialAssistanceCalculationStatus;
  title: string;
};

const FinancialAssistanceV5: React.FunctionComponent<FinancialAidV5FormProps> = (props) => {
  const formState = useFormState();

  return (
    <div>
      {props.handleNavigationBack && (
        <Prompt when={formState.dirty && !formState.submitSucceeded} message={LEAVE_FORM_WARNING} />
      )}
      <div className={classNames(typography.body, styles.screen)}>
        <div className={styles.header}>
          <div className={styles.headerLogo}>
            <Logo />
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.title}>Financial Assistance</div>
          {formState.submitFailed ? <FormBannerError /> : undefined}
          {!props.handleNavigationSubmit ? (
            props.showAlert ? (
              <FormAlert
                setShowAlert={props.setShowAlert}
                message={props.statement}
                title={props.title}
                status={
                  props.status === FinancialAssistanceCalculationStatus.APPROVED
                    ? AlertStatus.SUCCESS
                    : AlertStatus.WARNING
                }
              />
            ) : undefined
          ) : undefined}
          <div className={styles.subtitle}>Please complete all fields</div>
          <PatientInformationV5Table validatedValues={props.validatedValues} />
          <OrderingPhysicianV5Table />
          <ExtenuatingCircumstancesV5Table />
          <ConsentToApplicationV5Table />
          <div className={styles.footer}>
            {props.handleNavigationBack && (
              <div className={styles.backButton}>
                <Button
                  ariaLabel="financial-assistance-back"
                  type="button"
                  buttonType="secondary"
                  onClick={props.handleNavigationBack}
                  disabled={formState.submitting}
                >
                  Back
                </Button>
              </div>
            )}
            <Button loading={formState.submitting} ariaLabel="financial-assistance-submit" type="submit">
              Submit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const getAllFinancialAssistanceV5TableFormFields = (): FormTableField[] => {
  return patientInformationV5Fields.concat(
    orderingPhysicianV5Fields,
    consentToApplicationV5Fields,
    extenuatingCircumstancesV5Fields,
  );
};

const FinancialAssistanceV5Wrapper: React.FunctionComponent<NavigationFormProps> = (props) => {
  const selectedId = props.handleNavigationSubmit ? useQuery('orderId') : null;
  const [financialAidStatus, setFinancialAidStatus] = useState<FinancialAssistanceCalculationStatus>(
    FinancialAssistanceCalculationStatus.DENIED,
  );
  const [financialAidStatement, setFinancialAidStatement] = useState<string>('');
  const [financialAidTitle, setFinancialAidTitle] = useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);

  const onSubmit = React.useCallback(async (values: any, form: FormApi) => {
    try {
      setShowAlert(false);
      const authToken = requiresInternalAuthentication(values[RELATIONSHIP_TO_PATIENT_NAME_V4])
        ? await getOktaAccessToken()
        : undefined;
      if (props.validatedValues) {
        setReadOnlyValidatedValuesOnSubmit(values, patientInformationV5Fields, props.validatedValues);
      }
      deleteUnmetConditionalFields(values, getAllFinancialAssistanceV5TableFormFields());
      deleteFalsyStringFields(values);

      const { statement, status, title } = await submitFormGeneric<
        FinancialAidDto<FinancialAssistanceCalculationStatus>
      >(FINANCIAL_ASSISTANCE_V5_KEY, selectedId, values, authToken);

      if (props.handleNavigationSubmit) {
        const badgeValues: StatusBadgeProps = {
          text: statement,
          title: title,
          type: status,
        };
        props.handleNavigationSubmit(FINANCIAL_ASSISTANCE_V5_KEY, badgeValues);
      } else {
        setFinancialAidStatus(status);
        setFinancialAidStatement(statement);
        setFinancialAidTitle(title);
        setShowAlert(true);
        form.restart();
      }
    } catch (e) {
      setShowAlert(false);
      return { [FORM_ERROR]: 'Submission Failed' };
    }
  }, []);

  return (
    <PatientFormsTitleInjection>
      <FAOncoNeuroSelector {...props} formKey={FINANCIAL_ASSISTANCE_V5_KEY}>
        <Form onSubmit={onSubmit}>
          <FinancialAssistanceV5
            showAlert={showAlert}
            setShowAlert={setShowAlert}
            title={financialAidTitle}
            statement={financialAidStatement}
            status={financialAidStatus}
            {...props}
          />
        </Form>
      </FAOncoNeuroSelector>
    </PatientFormsTitleInjection>
  );
};

export default FinancialAssistanceV5Wrapper;
